import { useTranslation } from 'react-i18next'

import {
  FLOATING_ELEMENT_PLACEMENT,
  NOTIFICATION_TYPE,
} from '../../../../../constants/common'
import { useContentOverflow, useUserData } from '../../../../../hooks'
import { Avatar, AVATAR_HEIGHT_32 } from '../../../../Avatar'
import { FloatingTooltip } from '../../../../Floating'
import { NotificationInline } from '../../../../Notification'
import { getRentalPortalApplicantIdentityLabel } from '../../../../RentalPortalApplicant'
import Text from '../../../../Text'
import { Widget } from '../../../../Widget'
import { useApplicantProfileContext } from '../../RentalPortalApplicantProfile.hooks'
import { getApplicantIdentityAvatarProps } from '../ApplicantIdentityWidget'

import { ApplicantDecisionActions } from './ApplicantDecisionActions'
import { ApplicantDecisionLedgerRow } from './ApplicantDecisionLedgerRow'
import {
  QA_NAME_APPLICANT_DECISION_APPLICANT,
  QA_NAME_APPLICANT_DECISION_APPLICANT_AVATAR,
  QA_NAME_APPLICANT_DECISION_APPROVAL_RULE,
  QA_NAME_APPLICANT_DECISION_CONFLICT,
  QA_NAME_APPLICANT_DECISION_ESCALATION,
  QA_NAME_APPLICANT_DECISION_PANEL,
  QA_NAME_APPLICANT_DECISION_REVIEWER,
  QA_NAME_APPLICANT_DECISION_SELECTED_BY,
} from './ApplicantDecisionPanel.constants'
import { useApplicantDecisionAnchor } from './ApplicantDecisionPanel.hooks'
import {
  getApplicantDecisionActions,
  getApplicantDecisionApprovalPeopleRows,
  getApplicantDecisionApprovalsRow,
  getApplicantDecisionEscalationKey,
  getApplicantDecisionSelectedRow,
  getApplicantDecisionStepActions,
} from './ApplicantDecisionPanel.utils'

import * as styles from './ApplicantDecisionPanel.module.css'

function ApplicantDecisionPanel() {
  const { t } = useTranslation()

  const currentUser = useUserData()

  const { applicantData, decisionActions, isInitiated, isPreview } =
    useApplicantProfileContext()

  const { overflow, setContentOverflowRef } = useContentOverflow()

  const isPanelReady = isInitiated && Boolean(applicantData)

  const anchorRef = useApplicantDecisionAnchor(isPanelReady)

  const { conflictKey, handleClickAction } = decisionActions

  const applicantLabel = getRentalPortalApplicantIdentityLabel({
    applicant: applicantData,
    t,
  })

  const { approvalActions, selectActions } = getApplicantDecisionStepActions(
    getApplicantDecisionActions({
      applicantData,
      currentUserId: currentUser?.id,
      isPreview,
    })
  )

  const approvalPeopleRows = getApplicantDecisionApprovalPeopleRows({
    applicantData,
    t,
  })

  const escalationKey = getApplicantDecisionEscalationKey(applicantData)

  return (
    <Widget
      headerProps={{ contentProps: { actionsProps: { EditButton: null } } }}
      isSkeleton={!isPanelReady}
      qaName={QA_NAME_APPLICANT_DECISION_PANEL}
      title={t('RentalPortal.ApplicantDecisionTitleKey')}
    >
      <div className={styles.ApplicantDecisionPanel} ref={anchorRef}>
        <div className={styles.applicant}>
          <Avatar
            {...getApplicantIdentityAvatarProps(applicantData)}
            qaName={QA_NAME_APPLICANT_DECISION_APPLICANT_AVATAR}
            size={AVATAR_HEIGHT_32}
          />
          <FloatingTooltip
            content={applicantLabel}
            disabled={!overflow}
            placement={FLOATING_ELEMENT_PLACEMENT.top}
          >
            <Text
              className={styles.applicantName}
              qaName={QA_NAME_APPLICANT_DECISION_APPLICANT}
              ref={setContentOverflowRef}
            >
              {applicantLabel}
            </Text>
          </FloatingTooltip>
          <ApplicantDecisionActions
            actions={selectActions}
            className={styles.applicantActions}
            onClick={handleClickAction}
          />
        </div>

        {conflictKey ? (
          <NotificationInline
            qaName={QA_NAME_APPLICANT_DECISION_CONFLICT}
            text={t(conflictKey)}
            type={NOTIFICATION_TYPE.info}
          />
        ) : null}

        <div className={styles.ledger}>
          <ApplicantDecisionLedgerRow
            {...getApplicantDecisionSelectedRow({
              applicantData,
              currentUser,
              t,
            })}
            qaName={QA_NAME_APPLICANT_DECISION_SELECTED_BY}
            step={1}
          />
          <ApplicantDecisionLedgerRow
            {...getApplicantDecisionApprovalsRow({ applicantData, t })}
            qaName={QA_NAME_APPLICANT_DECISION_APPROVAL_RULE}
            step={2}
          >
            {approvalPeopleRows.length > 0 ? (
              <div className={styles.approvalPeople}>
                {approvalPeopleRows.map(({ id, ...rowProps }) => (
                  <ApplicantDecisionLedgerRow
                    {...rowProps}
                    key={id}
                    qaName={QA_NAME_APPLICANT_DECISION_REVIEWER}
                  />
                ))}
              </div>
            ) : null}
            <ApplicantDecisionActions
              actions={approvalActions}
              onClick={handleClickAction}
            />
          </ApplicantDecisionLedgerRow>
        </div>

        {escalationKey ? (
          <NotificationInline
            qaName={QA_NAME_APPLICANT_DECISION_ESCALATION}
            text={t(escalationKey)}
            type={NOTIFICATION_TYPE.warning}
          />
        ) : null}
      </div>
    </Widget>
  )
}

export default ApplicantDecisionPanel
